import { Feather } from '@expo/vector-icons';
import React from 'react';
import {
  Linking,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle
} from 'react-native';
import { PaperResult } from '../services/paperApi';

interface PaperCardProps {
  paper: PaperResult;
  style?: ViewStyle;
}

export default function PaperCard({ paper, style }: PaperCardProps) {
  const formatAuthors = (authors?: string[] | null) => {
    if (!authors || authors.length === 0) return 'Unknown Authors';
    if (authors.length <= 2) return authors.join(' & ');
    if (authors.length <= 5) return authors.join(', ');
    return `${authors.slice(0, 5).join(', ')}, et al.`;
  };

  const openLink = (url: string) => {
    Linking.openURL(url);
  };

  return (
    <View style={[styles.paperCard, style]}>
      <Text style={styles.paperTitle}>{paper.title || 'Untitled Paper'}</Text>

      <View style={styles.paperMeta}>
        <Feather name="users" size={16} color="#64748b" />
        <Text style={styles.paperMetaText}>
          {formatAuthors(paper.authors)}
        </Text>
      </View>

      <View style={styles.paperMeta}>
        <Feather name="calendar" size={16} color="#64748b" />
        <Text style={styles.paperMetaText}>
          {paper.venue || 'Unknown Venue'}
          {paper.year && ` (${paper.year})`}
        </Text>
      </View>

      <View style={styles.paperAccess}>
        {paper.is_open_access === true ? (
          <View style={styles.paperAccessContent}>
            <Feather name="unlock" size={16} color="#16a34a" />
            <Text style={styles.openAccessText}>Open Access</Text>
          </View>
        ) : paper.is_open_access === false ? (
          <View style={styles.paperAccessContent}>
            <Feather name="lock" size={16} color="#d97706" />
            <Text style={styles.closedAccessText}>Subscription Required</Text>
          </View>
        ) : (
          <View style={styles.paperAccessContent}>
            <Feather name="help-circle" size={16} color="#64748b" />
            <Text style={styles.unknownAccessText}>Access Unknown</Text>
          </View>
        )}
      </View>

      <View style={styles.paperLinks}>
        {paper.paper_url ? (
          <TouchableOpacity
            style={styles.paperLinkButton}
            onPress={() => openLink(paper.paper_url!)}
          >
            <Feather name="external-link" size={16} color="#1d4ed8" />
            <Text style={styles.paperLinkText}>View Paper</Text>
          </TouchableOpacity>
        ) : null}

        {paper.pdf_url ? (
          <TouchableOpacity
            style={[styles.paperLinkButton, styles.pdfButton]}
            onPress={() => openLink(paper.pdf_url!)}
          >
            <Feather name="file-text" size={16} color="#166534" />
            <Text style={[styles.paperLinkText, styles.pdfButtonText]}>
              PDF
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  paperCard: {
    backgroundColor: 'white',
    borderWidth: 2,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
  },
  paperTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 16,
    lineHeight: 28,
  },
  paperMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  paperMetaText: {
    color: '#64748b',
    fontSize: 16,
  },
  paperAccess: {
    marginTop: 8,
    marginBottom: 16,
  },
  paperAccessContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  openAccessText: {
    color: '#16a34a',
    fontWeight: '500',
    fontSize: 16,
  },
  closedAccessText: {
    color: '#d97706',
    fontWeight: '500',
    fontSize: 16,
  },
  unknownAccessText: {
    color: '#64748b',
    fontWeight: '500',
    fontSize: 16,
  },
  paperLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  paperLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#eff6ff',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  paperLinkText: {
    color: '#1d4ed8',
    fontWeight: '500',
    fontSize: 16,
  },
  pdfButton: {
    backgroundColor: '#f0fdf4',
  },
  pdfButtonText: {
    color: '#166534',
  },
});